import { useModelsToOptionsWithData } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import {
  GeneralReport,
  useGetGeneralReportsSuspenseInfinite,
} from 'ap-openapi';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';

type Options = {
  label: string;
  value: string;
};

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<FormSelectProps<TFieldValues>['selectProps'], 'options'>;
  per?: number;
  validateMaxCount?: number;
  defaultOptions?: Options[];
  createdByOfficeMemberId?: string;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'>;

const _GeneralReportsSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  per = 50,
  createdByOfficeMemberId,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );

  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetGeneralReportsSuspenseInfinite(
    {
      query,
      status: 'approved',
      per: per.toString(),
      created_by_office_member_id: createdByOfficeMemberId,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return url.searchParams.get('page');
        },
      },
      request: {
        headers: {
          agentApplicantResource: 'report_number_for_expense',
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(
    () => getPageData(apiData, 'ap_general_reports'),
    [apiData]
  );
  const options = useModelsToOptionsWithData<GeneralReport>(
    data,
    query,
    defaultOptions,
    {
      labelKeys: ['title'],
      value: 'number',
    }
  );
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const selectProps: SelectProps = useMemo(() => {
    return {
      onSearchDebounceWait: 600,
      comboBox: true,
      placeholder: t('general_reports_placeholder'),
      inputPlaceholder: t('general_reports_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      fetchMore: fetchMore,
      loading: isLoading,
      selectAll: undefined,
      selectAllLabel: undefined,
      mode: 'multiple',
      additionalOption: true,
    };
  }, [_selectProps, isLoading, options, t, onSearch, fetchMore]);

  return <FormSelect<TFieldValues> selectProps={selectProps} {...rest} />;
};
_GeneralReportsSelect.displayName = '_GeneralReportsSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const GeneralReportsSelect = memo((props) => (
  <_GeneralReportsSelect {...props} />
)) as typeof _GeneralReportsSelect;
GeneralReportsSelect.displayName = 'GeneralReportsSelect';
